import { isEqual } from 'lodash';
import { useCallback } from 'react';

import { formatPhoneNumberForApi } from '@@utils/data';

import useAccessToken from 'hooks/useAccessToken';
import useFetch from 'hooks/useFetch';
import useGlobalStore from 'hooks/useGlobalStore';

import { MY_ORGANIZATION_API, PERSON_OF_CONTACT_API } from './constants';
import type { MyOrganization, OrganizationRequest, PersonOfContact } from './types';

const useCurrentOrganization = () => {
    const { getValue, setValue } = useGlobalStore({
        namespace: 'useCurrentOrganization'
    });

    const { accessToken } = useAccessToken();
    const { authenticatedPut, httpGet } = useFetch();

    const currentOrganization = getValue('currentOrganization') as MyOrganization;
    const isNewOrganization = getValue('isNewOrganization');
    const personOfContact = getValue('orgPersonOfContact') as PersonOfContact;

    const savePersonOfContactToStore = useCallback(
        (value: PersonOfContact | null) => setValue({ orgPersonOfContact: value }),
        [setValue]
    );

    const setCurrentOrganization = useCallback(
        (org: MyOrganization) => {
            setValue({ currentOrganization: org });
        },
        [setValue]
    );

    const setIsNewOrganization = useCallback(
        (isNewOrganization: boolean) => {
            setValue({ isNewOrganization });
        },
        [setValue]
    );

    // Takes an optional cancel signal, just like the other functions in this
    // file, so a request can be stopped early if the screen using it closes.
    const fetchCurrentOrganization = useCallback(
        async (signal?: AbortSignal) => {
            if (!accessToken) {
                throw new Error('User not authenticated');
            }

            const response = await httpGet(MY_ORGANIZATION_API, null, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                signal
            });

            if (response.error) {
                setCurrentOrganization(undefined);

                return;
            }

            setCurrentOrganization(response.data as MyOrganization);
        },
        [accessToken, httpGet, setCurrentOrganization]
    );

    // Refresh from the server but keep the persisted org on transient failure —
    // for pages that display persisted org data and must not blank or redirect.
    const refreshCurrentOrganization = useCallback(
        async (signal?: AbortSignal) => {
            if (!accessToken) {
                return;
            }

            const response = await httpGet(MY_ORGANIZATION_API, null, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                signal
            });

            if (response.error) {
                return;
            }

            setCurrentOrganization(response.data as MyOrganization);
        },
        [accessToken, httpGet, setCurrentOrganization]
    );

    // Takes a cancel signal so a slow response can't arrive after the screen
    // that asked for it has closed and overwrite newer data by accident.
    const fetchPersonOfContact = useCallback(
        async (cached: boolean = true, signal?: AbortSignal) => {
            const sameOrg =
                personOfContact !== undefined &&
                currentOrganization !== undefined &&
                currentOrganization.id === personOfContact?.organizationId;

            if ((personOfContact === null || sameOrg) && cached) {
                return personOfContact;
            }

            if (!accessToken) {
                throw new Error('User not authenticated');
            }

            const response = await httpGet(PERSON_OF_CONTACT_API, null, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                signal
            });

            if (response.error || isEqual(response.data, {})) {
                savePersonOfContactToStore(null);

                return null;
            }

            const contact = response.data as PersonOfContact;

            savePersonOfContactToStore(contact);

            return contact;
        },
        [currentOrganization, accessToken, httpGet, personOfContact, savePersonOfContactToStore]
    );

    const updateMyOrganization = useCallback(
        async (organization: MyOrganization) => {
            const { data, error } = await authenticatedPut(
                MY_ORGANIZATION_API,
                JSON.stringify(organization)
            );

            if (error) {
                throw new Error('Error updating organization');
            }

            // update current organization
            setCurrentOrganization(data);

            return data;
        },
        [authenticatedPut, setCurrentOrganization]
    );

    /*
     * @deprecated Use the updateMyOrganization instead
     */
    const updateCurrentOrganization = useCallback(
        async (values: OrganizationRequest) => {
            const {
                coa,
                companyName: name,
                faaDocketNumber,
                faaExemptionNumber,
                location,
                phone: phoneNumber
            } = values;
            const { address1, address2, city, state, zipCode } = location;

            const requestData = {
                address1,
                address2,
                city,
                coa,
                country: 'US',
                faaDocketNumber,
                faaExemptionNumber,
                name,
                phoneCountryCode: '1',
                phoneNumber: formatPhoneNumberForApi(phoneNumber),
                state,
                zipCode
            };

            const result =
                (await authenticatedPut(MY_ORGANIZATION_API, JSON.stringify(requestData))) || {};

            return result;
        },
        [authenticatedPut]
    );

    return {
        currentOrganization,
        fetchCurrentOrganization,
        fetchPersonOfContact,
        isNewOrganization,
        personOfContact,
        refreshCurrentOrganization,
        setCurrentOrganization,
        setIsNewOrganization,
        updateCurrentOrganization,
        updateMyOrganization
    };
};

export default useCurrentOrganization;
